import { useState, useEffect, useCallback, useRef } from 'react';
import { cn } from '@/lib/utils';

interface SystemBootOverlayProps {
  active: boolean;
  onComplete: () => void;
}

const BOOT_STEPS = [
  'Decision Engine wird initialisiert',
  'Struktur wird vorbereitet',
  'Analyse bereitgestellt',
];

export const SystemBootOverlay = ({ active, onComplete }: SystemBootOverlayProps) => {
  const [phase, setPhase] = useState<'idle' | 'entering' | 'running' | 'exiting' | 'done'>('idle');
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [stepVisible, setStepVisible] = useState(false);
  const onCompleteRef = useRef(onComplete);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  onCompleteRef.current = onComplete;

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  const runSequence = useCallback(() => {
    clearTimers();

    // Phase 1: Overlay enters (0-150ms)
    setPhase('entering');
    setCurrentStepIndex(0);
    setStepVisible(false);

    // Phase 2: Start showing steps (150ms)
    const t1 = setTimeout(() => {
      setPhase('running');
      setStepVisible(true);
    }, 150);

    // Phase 2b: Step 2 (450ms)
    const t2 = setTimeout(() => {
      setStepVisible(false);
      const step2Timer = setTimeout(() => {
        setCurrentStepIndex(1);
        setStepVisible(true);
      }, 80);
      timersRef.current.push(step2Timer);
    }, 450);

    // Phase 2c: Step 3 (750ms)
    const t3 = setTimeout(() => {
      setStepVisible(false);
      const step3Timer = setTimeout(() => {
        setCurrentStepIndex(2);
        setStepVisible(true);
      }, 80);
      timersRef.current.push(step3Timer);
    }, 750);

    // Phase 3: Exit (1000ms)
    const t4 = setTimeout(() => {
      setPhase('exiting');
    }, 1000);

    // Phase 4: Done (1200ms)
    const t5 = setTimeout(() => {
      setPhase('done');
      onCompleteRef.current();
    }, 1200);

    timersRef.current.push(t1, t2, t3, t4, t5);
  }, [clearTimers]);

  useEffect(() => {
    if (active) {
      runSequence();
      return clearTimers;
    }
  }, [active, runSequence, clearTimers]);

  // Reset when deactivated
  useEffect(() => {
    if (!active) {
      clearTimers();
      setPhase('idle');
      setCurrentStepIndex(0);
      setStepVisible(false);
    }
  }, [active, clearTimers]);

  if (phase === 'idle' || phase === 'done') return null;

  return (
    <div
      className={cn(
        'fixed inset-0 z-[999] flex flex-col items-center justify-center bg-background transition-opacity duration-200',
        phase === 'entering' && 'opacity-0 animate-in fade-in duration-150',
        phase === 'running' && 'opacity-100',
        phase === 'exiting' && 'opacity-0 transition-opacity duration-200',
      )}
      style={{
        background: `
          radial-gradient(circle at 50% 40%, hsl(var(--primary) / 0.03), transparent 60%),
          hsl(var(--background))
        `,
      }}
    >
      {/* Brand anchor */}
      <span
        className="text-[11px] font-medium tracking-[0.12em] uppercase text-muted-foreground/60 mb-8"
      >
        Quentari
      </span>

      {/* Boot step text */}
      <div className="h-6 flex items-center justify-center">
        <p
          className={cn(
            'text-sm text-muted-foreground transition-all duration-200',
            stepVisible
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-1',
          )}
        >
          {BOOT_STEPS[currentStepIndex]}
        </p>
      </div>

      {/* Minimal pulse dot */}
      <div className="mt-6 flex items-center gap-1.5">
        <span className="h-1 w-1 rounded-full bg-primary/40 animate-pulse" />
        <span className="h-1 w-1 rounded-full bg-primary/30 animate-pulse [animation-delay:150ms]" />
        <span className="h-1 w-1 rounded-full bg-primary/20 animate-pulse [animation-delay:300ms]" />
      </div>
    </div>
  );
};
